/**
 * API Service
 *
 * Centralized Axios-based API client with:
 * - Runtime configuration integration
 * - Automatic authentication token injection
 * - Unified error handling and transformation
 * - Request/response interceptors
 * - Datadog RUM logging
 */

import axios, {
	type AxiosError,
	type AxiosInstance,
	type AxiosRequestConfig,
	type AxiosResponse,
	type CreateAxiosDefaults,
	type InternalAxiosRequestConfig,
} from "axios";
import type { ResolvedConfig } from "../config";
import { getConfig, getConfigSafe } from "../config";
import { markLocalEntityWriteFromResponse } from "../features/live-updates/local-entity-write-registry";
import { authStore } from "../stores/auth-store";
import {
	type ApiError,
	logError,
	transformAxiosError,
} from "../utils/error-transformer";

// Datadog RUM types
declare global {
	interface Window {
		DD_RUM?: {
			addError: (error: Error, context?: Record<string, unknown>) => void;
			addAction: (name: string, context?: Record<string, unknown>) => void;
			setUser: (user: Record<string, unknown>) => void;
		};
	}
}

// ============================================================================
// Type Definitions
// ============================================================================

/**
 * API endpoint keys from configuration
 */
type ApiEndpointKey = keyof ResolvedConfig["api"]["endpoints"];

// ============================================================================
// Configuration Helpers
// ============================================================================

/**
 * Get API configuration from runtime config
 */
export function getApiConfig() {
	const config = getConfig();
	if (!config) {
		throw new Error("Configuration not loaded. Cannot get API config.");
	}

	const apiConfig = {
		baseUrl: config.api.baseUrl,
		timeout: config.api.timeout,
		debug: config.api.debug,
		headers: {
			"Content-Type": "application/json",
		},
	} as const;

	if (config.api.debug) {
		console.log("🔧 API Config:", {
			baseUrl: apiConfig.baseUrl,
			timeout: apiConfig.timeout,
			environment: config.app.environment,
		});
	}

	return apiConfig;
}

/**
 * Get a configured API endpoint URL by key
 *
 * @example
 * const endpoint = getApiEndpoint('demo')
 * await apiService.get(`${endpoint}/todos`)
 */
export function getApiEndpoint(endpointKey: ApiEndpointKey): string {
	const config = getConfig();
	if (!config) {
		throw new Error("Configuration not loaded. Cannot get API endpoint.");
	}

	const endpoint = config.api.endpoints[endpointKey];
	if (!endpoint) {
		throw new Error(`API endpoint '${endpointKey}' not found in configuration`);
	}

	if (config.api.debug) {
		console.log(`🔧 API Endpoint [${endpointKey}]:`, endpoint);
	}

	return endpoint;
}

/**
 * Builds the `organization-id` request header for org-scoped API calls.
 *
 * @param organizationId - Organization UUID from route or request context.
 * @returns Header map when id is non-empty; otherwise undefined.
 */
export function organizationIdHeader(
	organizationId?: string,
): Record<string, string> | undefined {
	if (organizationId && organizationId.length > 0) {
		return { "organization-id": organizationId };
	}
	return undefined;
}

// ============================================================================
// Axios Instance & Interceptors
// ============================================================================

/**
 * Singleton Axios instance
 */
let axiosInstance: AxiosInstance | null = null;

/**
 * Create and configure Axios instance
 */
function createAxiosInstance(): AxiosInstance {
	const config = getConfig();

	const axiosConfig: CreateAxiosDefaults = {
		baseURL: config.api.baseUrl,
		timeout: config.api.timeout,
		headers: {
			"Content-Type": "application/json",
			Accept: "application/json",
		},
		withCredentials: true, // Enable cookies for authentication
		paramsSerializer: {
			// Serialize arrays with bracket notation: param[0]=value1&param[1]=value2
			indexes: null, // This enables bracket notation for arrays
		},
	};

	const instance = axios.create(axiosConfig);

	// Setup interceptors
	// console.log("🔧 API Service: Setting up interceptors");
	setupInterceptors(instance);

	if (config.api.debug) {
		console.log("✅ API Service: Axios instance created", {
			baseURL: axiosConfig.baseURL,
			timeout: axiosConfig.timeout,
		});
	}

	return instance;
}

/**
 * Get the singleton Axios instance
 */
function getAxiosInstance(): AxiosInstance {
	if (!axiosInstance) {
		const config = getConfigSafe();
		if (!config) {
			throw new Error(
				"API Service: Configuration not loaded. Ensure config is initialized before making API requests.",
			);
		}
		axiosInstance = createAxiosInstance();
	}
	return axiosInstance;
}

/**
 * Update Axios configuration at runtime
 */
export function updateApiConfig(): void {
	// console.log("🔄 API Service: Updating configuration...");
	axiosInstance = createAxiosInstance();
	// console.log("✅ API Service: Configuration updated");
}

/**
 * Reset Axios instance (forces recreation on next access)
 */
export function resetApiService(): void {
	axiosInstance = null;
	console.log("🔧 API Service: Instance reset");
}

// ============================================================================
// Interceptors
// ============================================================================

/**
 * Request interceptor - adds auth, correlation ID, logging
 */
function onRequest(
	config: InternalAxiosRequestConfig,
): InternalAxiosRequestConfig {
	const runtimeConfig = getConfigSafe();

	// Add correlation ID for request tracing
	if (!config.headers["X-Correlation-ID"]) {
		config.headers["X-Correlation-ID"] = crypto.randomUUID();
	}

	// Auth token is handled by the backend via cookies with withCredentials: true
	// If you need to add a bearer token manually, uncomment and configure:
	// const authState = authStore.getState();
	// if (authState.isAuthenticated) {
	//   config.headers.Authorization = `Bearer ${yourTokenHere}`;
	// }

	// Debug logging
	if (runtimeConfig?.api.debug) {
		console.log("🌐 API Request:", {
			method: config.method?.toUpperCase(),
			url: config.url,
			baseURL: config.baseURL,
			correlationId: config.headers["X-Correlation-ID"],
			hasAuth: !!config.headers.Authorization,
		});
	}

	return config;
}

/**
 * Request error interceptor
 */
function onRequestError(error: unknown): Promise<never> {
	console.error("❌ API Service: Request setup failed:", error);
	return Promise.reject(error);
}

/**
 * Response success interceptor
 */
function onResponse(response: AxiosResponse) {
	// console.log("🔧 API Service: Response success interceptor", response);
	const runtimeConfig = getConfigSafe();
	markLocalEntityWriteFromResponse(response);

	if (runtimeConfig?.api.debug) {
		console.log("✅ API Response:", {
			status: response.status,
			statusText: response.statusText,
			url: response.config.url,
			correlationId: response.config.headers?.["X-Correlation-ID"],
		});
	}

	return response;
}

/**
 * URL patterns for which a 404 response is a normal, non-error condition.
 *
 * The API returns 404 for these endpoints when the resource simply does not
 * exist (e.g. a user who has never uploaded an avatar), rather than to signal
 * a problem. Matching requests are excluded from error logging so that
 * developer consoles are not flooded with noise for expected "not found"
 * responses. The error still propagates normally — callers are responsible for
 * handling the null/absent case themselves.
 *
 * Add entries here (not at the call site) when a new 404-is-normal endpoint is
 * introduced in the API.
 */
const SILENT_404_URL_PATTERNS: RegExp[] = [
	// Example: avatar not found — a 404 means the user has no avatar, which is a valid state.
	/\/users\/[^/]+\/avatar$/,
	// Sub-contractor sub-org access: 404 means the assignment doesn't exist (already unassigned).
	/\/sub-contractors\/[^/]+\/access\/[^/]+$/,
	// Type attachment requirement: 404 means the type has no requirement (None).
	/\/(jobs|tickets|assets|records)\/types\/[^/]+\/attachment-requirement$/,
];

/**
 * Response error interceptor
 */
async function onResponseError(error: unknown): Promise<never> {
	// console.log("🔧 API Service: Response error interceptor", error);
	const runtimeConfig = getConfigSafe();

	// Only handle Axios errors
	if (!error || typeof error !== "object" || !("isAxiosError" in error)) {
		return Promise.reject(error);
	}

	const axiosError = error as AxiosError;
	// console.log("🔧 API Service: Axios error", axiosError);
	const status = axiosError.response?.status;
	const requestUrl = axiosError.config?.url || "";
	// console.log("🔧 API Service: Status", status);

	// Handle 401 Unauthorized - clear local auth and send to web /login
	// (browser only; no window on SSR/Node). Does not call API log-out —
	// that endpoint requires authentication and fails when the session is dead.
	// BUT SKIP if:
	// 1. This is the logout endpoint itself (prevents infinite loop)
	// 2. We're already on the login page (prevents reload loop)
	// 3. We're on the OAuth callback page (session is being established)
	// 4. The user is not currently authenticated (auth init handles that path)
	const isBrowser = typeof window !== "undefined";
	const currentPath = isBrowser ? window.location.pathname : "";
	const isLogoutEndpoint = requestUrl.includes("/authentication/log-out");
	const isOnLoginPage = currentPath === "/login";
	const isOnSignedOutPage = currentPath === "/signed-out";
	const isOnCallbackPage = currentPath === "/auth/callback";
	const isCurrentlyAuthenticated = authStore.getState().isAuthenticated;
	const skipUnauthorizedRedirect = Boolean(
		(
			axiosError.config as
				| (AxiosRequestConfig & { skipUnauthorizedRedirect?: boolean })
				| undefined
		)?.skipUnauthorizedRedirect,
	);
	if (
		isBrowser &&
		status === 401 &&
		!skipUnauthorizedRedirect &&
		!isLogoutEndpoint &&
		!isOnLoginPage &&
		!isOnSignedOutPage &&
		!isOnCallbackPage &&
		isCurrentlyAuthenticated
	) {
		console.warn(
			"🔒 API Service: Unauthorized (401) - Clearing auth and redirecting to /login",
		);

		authStore.getState().handleUnauthorized();
	}

	// Handle 403 Forbidden
	if (status === 403) {
		console.warn("⛔ API Service: Forbidden (403) - Access denied");
	}

	// Transform error to unified format
	const transformedError: ApiError = transformAxiosError(axiosError);

	// Skip logging for endpoints where 404 is a normal, expected response
	// (see SILENT_404_URL_PATTERNS above). The error still rejects normally —
	// only the console noise is suppressed.
	const isSilent404 =
		status === 404 &&
		SILENT_404_URL_PATTERNS.some((pattern) => pattern.test(requestUrl));

	if (!isSilent404) {
		// Log error (respects debug mode)
		logError(transformedError, runtimeConfig?.api.debug ?? false);

		// Log to Datadog RUM if available (browser only)
		if (typeof window !== "undefined" && window.DD_RUM) {
			try {
				window.DD_RUM.addError(transformedError, {
					type: "api_error",
					code: transformedError.code,
					status: transformedError.status,
					correlationId: transformedError.correlationId,
					endpoint: axiosError.config?.url,
					method: axiosError.config?.method,
				});
			} catch (ddError) {
				console.warn("Failed to log error to Datadog:", ddError);
			}
		}
	}

	return Promise.reject(transformedError);
}

/**
 * Setup interceptors on an Axios instance
 */
function setupInterceptors(instance: AxiosInstance): void {
	// Request interceptor
	instance.interceptors.request.use(onRequest, onRequestError);

	// Response interceptor
	instance.interceptors.response.use(onResponse, onResponseError);

	const config = getConfigSafe();
	if (config?.api.debug) {
		console.log("✅ API Service: Interceptors configured");
	}
}

// ============================================================================
// API Service Export
// ============================================================================

/**
 * Main API Service
 *
 * Type-safe HTTP client with automatic error handling and authentication
 *
 * @example
 * // GET request
 * const users = await apiService.get<User[]>('/users')
 *
 * // POST request
 * const newUser = await apiService.post<User>('/users', { name: 'John' })
 *
 * // With custom config
 * const data = await apiService.get('/data', {
 *   params: { page: 1, limit: 10 }
 * })
 */
export const apiService = {
	/**
	 * GET request
	 */
	get: async <T = unknown>(
		url: string,
		config?: AxiosRequestConfig,
	): Promise<T> => {
		const instance = getAxiosInstance();
		const response = await instance.get<T>(url, config);
		return response.data;
	},

	/**
	 * GET request that keeps response headers (file name lives on Content-Disposition).
	 */
	getResponse: async <T = unknown>(
		url: string,
		config?: AxiosRequestConfig,
	): Promise<AxiosResponse<T>> => {
		const instance = getAxiosInstance();
		return instance.get<T>(url, config);
	},

	/**
	 * POST request
	 */
	post: async <T = unknown>(
		url: string,
		data?: unknown,
		config?: AxiosRequestConfig,
	): Promise<T> => {
		const instance = getAxiosInstance();
		const response = await instance.post<T>(url, data, config);
		return response.data;
	},

	/**
	 * PUT request
	 */
	put: async <T = unknown>(
		url: string,
		data?: unknown,
		config?: AxiosRequestConfig,
	): Promise<T> => {
		const instance = getAxiosInstance();
		const response = await instance.put<T>(url, data, config);
		return response.data;
	},

	/**
	 * PATCH request
	 */
	patch: async <T = unknown>(
		url: string,
		data?: unknown,
		config?: AxiosRequestConfig,
	): Promise<T> => {
		const instance = getAxiosInstance();
		const response = await instance.patch<T>(url, data, config);
		return response.data;
	},

	/**
	 * DELETE request
	 */
	delete: async <T = unknown>(
		url: string,
		config?: AxiosRequestConfig & { skipUnauthorizedRedirect?: boolean },
	): Promise<T> => {
		const instance = getAxiosInstance();
		const response = await instance.delete<T>(url, config);
		return response.data;
	},

	/**
	 * HEAD request
	 */
	head: async (url: string, config?: AxiosRequestConfig) => {
		const instance = getAxiosInstance();
		return instance.head(url, config);
	},

	/**
	 * OPTIONS request
	 */
	options: async (url: string, config?: AxiosRequestConfig) => {
		const instance = getAxiosInstance();
		return instance.options(url, config);
	},
};

// ============================================================================
// Development Utilities
// ============================================================================

if (import.meta.env.DEV) {
	// Make API service available globally for debugging
	(globalThis as Record<string, unknown>).__apiService = {
		service: apiService,
		updateConfig: updateApiConfig,
		reset: resetApiService,
		getConfig: getApiConfig,
		getEndpoint: getApiEndpoint,
	};

	// console.log(
	// 	"🔧 API Service: Debug utilities available at window.__apiService",
	// );
}

// ============================================================================
// Type Exports
// ============================================================================

export type { ApiError } from "../utils/error-transformer";
